/**
 * Tipos wire da Tela 8 (Iniciar Conversa Proativa) — Brief 2B B7 (PR #301).
 *
 * Hand-written (R15) espelhando os DTOs REAIS do módulo do Engine
 * `apps/engine/src/modules/proactive-conversations/` (confirmado nos arquivos,
 * não via agente — lição da Tela 2). NÃO codegen.
 *
 * Achados da triangulação:
 *   - `MetaCategory` (POST) = customer_care|service|utility. As categorias do
 *     `WhatsAppTemplatesService` são OUTRO enum (utility|marketing|authentication);
 *     na prática só `utility` tem template no fallback dev.
 *   - `channel` no POST é `ChannelKind` kebab; `Identity.channel` (getPerson) vem
 *     CRU do Prisma (snake). O adapter mapeia snake↔kebab.
 *   - D9: `GET /templates` ANOTA `{ name }[]` no controller, mas o service devolve
 *     o `TemplateSummary` inteiro (incl. `bodyPreview`). Tipamos `name` obrigatório
 *     + o resto opcional — fiel ao contrato-piso E à realidade runtime.
 */

import type { ChannelKind } from '@lume/shared';

/** Categorias Meta aceitas no v1 do POST (subconjunto de MetaSessionType). */
export type MetaCategory = 'customer_care' | 'service' | 'utility';

/** Origens polimórficas da iniciação. `cold` = porta fria (só pessoa). */
export type ProactiveOriginType =
  | 'pendency'
  | 'commitment'
  | 'appointment'
  | 'conversation'
  | 'cold';

/** Origens que pré-preenchem via GET /context (cold excluído). */
export type ContextOriginType = Exclude<ProactiveOriginType, 'cold'>;

/** Canal de wire (kebab) — reusa o canônico do shared. */
export type ProactiveChannel = ChannelKind;

/** Códigos de bloqueio do POST (422 `{ code, reason, message }`). */
export type ProactiveBlockedCode =
  | 'category_not_allowed'
  | 'adversarial_blocked'
  | 'no_identity'
  | 'template_not_found'
  | 'recipient_restricted';

/**
 * Causa da recusa, na forma que o wire devolve (R-G9 · +oposição na fatia R-G5).
 *
 * `lgpd-opposition` não é mais uma preferência ao lado das outras duas: é direito exercido
 * formalmente (art. 18 §2). Quem lê o aviso precisa da distinção.
 */
export type RecipientRestrictionCause = 'opt-out-flag' | 'self-restriction' | 'lgpd-opposition';

/**
 * Resposta de `GET /v1/proactive-conversations/recipient-status` (R-G9).
 *
 * Booleano + causa, e nada mais: o servidor não manda o id do registro nem a data. Quem responde
 * é o MESMO predicado que a porta consulta — o aviso não pode discordar dela.
 */
export interface RecipientStatus {
  restricted: boolean;
  cause?: RecipientRestrictionCause;
}

/**
 * Item de `GET /v1/proactive-conversations/templates?category=`. `name` é o
 * identificador (vai como `templateId` no POST). `bodyPreview` traz os
 * placeholders `{{1}}..{{N}}` (D9 — opcional por fidelidade ao contrato-piso).
 */
export interface ProactiveTemplate {
  name: string;
  bodyPreview?: string;
  category?: string;
  language?: string;
  status?: string;
}

/** Resposta de `GET /v1/proactive-conversations/context`. */
export interface ProactiveContext {
  person: { id: string; displayName: string } | null;
  reasonHint: string;
  reference: { type: ContextOriginType; id: string };
}

/** Body de `POST /v1/proactive-conversations`. */
export interface InitiateProactivePayload {
  personId: string;
  channel: ProactiveChannel;
  originType: ProactiveOriginType;
  originId?: string;
  metaCategory: MetaCategory;
  templateId: string;
  templateVariables?: Record<string, string>;
}

/** Resposta 201 do POST. */
export interface InitiateProactiveResponse {
  conversationId: string;
}

/**
 * Erro tipado lançado pelo api-client quando o POST devolve 422. `code` é
 * `string` (não a união) para tolerar códigos novos do backend — os 4 conhecidos
 * estão em `ProactiveBlockedCode` e são mapeados em `blockedMessage`.
 */
export class ProactiveBlockedError extends Error {
  readonly code: string;
  readonly reason: string;

  constructor(code: string, reason: string, message: string) {
    super(message);
    this.name = 'ProactiveBlockedError';
    this.code = code;
    this.reason = reason;
  }
}
